import { getTranslatedProperty } from "@shopware/helpers";
import type { Schemas } from "#shopware";

type JsonLDObject = { [key: string]: JsonLDValue };
type JsonLDValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | JsonLDObject
    | JsonLDValue[];

interface Breadcrumb {
    name: string;
    path?: string;
}

export function useListingJsonLD(
    products: Schemas["Product"][],
    options: {
        categoryName?: string;
        breadcrumbs?: Breadcrumb[];
    } = {},
): Record<string, never> {
    const requestUrl = useRequestURL();
    const baseUrl = requestUrl.origin;

    const scripts: Array<{ type: string; innerHTML: string }> = [];

    if (products.length) {
        const itemListJsonLd: JsonLDObject = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            ...(options.categoryName ? { name: options.categoryName } : {}),
            numberOfItems: products.length,
            itemListElement: products.map(
                (product: Schemas["Product"], index: number) => {
                    const firstSeoUrl = product.seoUrls?.[0];
                    const productUrl = firstSeoUrl?.seoPathInfo
                        ? `${baseUrl}/${firstSeoUrl.seoPathInfo}`
                        : `${baseUrl}/detail/${product.id}`;

                    return {
                        "@type": "ListItem",
                        position: index + 1,
                        url: productUrl,
                        name: getTranslatedProperty(product, "name"),
                        image: product.cover?.media?.url || undefined,
                    };
                },
            ),
        };
        scripts.push({
            type: "application/ld+json",
            innerHTML: JSON.stringify(itemListJsonLd),
        });
    }

    if (options.breadcrumbs?.length) {
        const breadcrumbJsonLd: JsonLDObject = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: baseUrl,
                },
                ...options.breadcrumbs.map(
                    (crumb: Breadcrumb, index: number) => ({
                        "@type": "ListItem" as const,
                        position: index + 2,
                        name: crumb.name,
                        ...(crumb.path
                            ? { item: `${baseUrl}${crumb.path}` }
                            : {}),
                    }),
                ),
            ],
        };
        scripts.push({
            type: "application/ld+json",
            innerHTML: JSON.stringify(breadcrumbJsonLd),
        });
    }

    useHead(() => ({
        script: scripts,
    }));

    return {};
}